import React, {
	useState,
	useCallback,
	useContext,
	useEffect,
	ReactElement,
	useRef
} from 'react'
import { useDropzone } from 'react-dropzone'
import { BlobServiceClient, ContainerClient } from '@azure/storage-blob'
import RequestsRepository from '@services/repositories/requests.repository'
import { joinClasses, makeClasses } from '@utils/styles'
import Link from '@components/ui/link'
import * as pageUtils from '@components/ui/requestFiles/__index.utils'
import {
	AppContextProps,
	AppStateContext
} from '@components/layouts/DynamicLayout'
import { formatStrapiText } from '@utils/methods'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { Icon } from '@components/ui/Icon'
import { closeIcon, pdfIcon, wordIcon, excelIcon } from '@images/icons'
import { RequestImgUrl } from '@utils/request'
import { formatFileSize } from '@components/ui/Attachment/__index.utils'
import { Colors } from '@utils/css-variables'
import RequiredIcon from '@components/ui/requiredIcon'
import Text from '@components/ui/text'
import Error from '@components/requestForms/inputs/error.input'
import { thirdStepSubSteps } from '@templates/requests/create_tn'
import { RepereIntegrationRepository } from '@services/repositories/_miscellaneous.repository'
import config from '@utils/config'
import Notification from '@components/ui/notification'

type props = {
	hasError: boolean
	files: Set<File> // Fichiers
	uploadedFilesMap: Map<string, string> | undefined // Nom et URLs des fichiers déjà uploadés
	setUploadedFilesMap: (newMap: Map<string, string>) => void
	children?: ReactElement
	name?: string
	title?: string
	subtitle?: string
	required?: boolean
	disabled?: boolean
	totalFilesCount?: number
	removeSectionMarginTop?: boolean
	removeSectionPaddingTop?: boolean
	maxFiles?: number
	minFiles?: number
	namingValidation?: string
	formPosition?: number
	subSteps?: thirdStepSubSteps
	description?: string
	onSetHasError: (hasError: boolean, inputName: string) => void
	onSetFiles: (newFiles: Set<File>) => void
	isSummary?: boolean
	removePadding?: boolean
	equivalentMargin?: boolean
	fieldWarnings?: Record<string, boolean>
	showFilesCountWarning?: boolean
	toPrint?: boolean
}

const BlobUploader: React.FC<props> = ({
	name = '',
	children,
	hasError,
	title,
	subtitle,
	required,
	disabled,
	totalFilesCount,
	files,
	uploadedFilesMap = new Map(),
	setUploadedFilesMap,
	maxFiles,
	formPosition,
	subSteps,
	onSetFiles,
	onSetHasError,
	isSummary,
	description,
	fieldWarnings,
	showFilesCountWarning = false,
	toPrint
}) => {
	type Classes = {
		container: string
		dropzone: string
		fileList: string
		uploadButton: string
		progressContainer: string
		progressBarContainer: string
		progressBar: string
		uploadedFiles: string
		errorContainer: string
		loader: string
		justifyContent: string
		fileItemStatus: string
		fileItemName: string
		fileItemDetailsSummary: string
	}

	const classes: Classes = makeClasses({
		container: {
			padding: '16px',
			border: '1px solid #ccc',
			borderRadius: '8px',
			boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
			marginBottom: '10px !important',
			marginTop: '10px !important'
		},
		dropzone: {
			border: '2px dashed #007bff',
			padding: '20px',
			borderRadius: '8px',
			textAlign: 'center',
			cursor: 'pointer',
			transition: 'background 0.2s',

			'&:hover': {
				background: '#f8f9fa'
			}
		},

		fileList: {
			marginTop: '16px',
			ul: {
				listStyle: 'none',
				padding: 0
			}
		},

		uploadButton: {
			background: '#007bff',
			color: 'white',
			padding: '8px 16px',
			border: 'none',
			borderRadius: '4px',
			cursor: 'pointer',
			marginTop: '10px',

			'&:hover': {
				background: '#0056b3'
			},

			'&:disabled': {
				background: '#aaa',
				cursor: 'not-allowed'
			}
		},

		progressContainer: {
			marginTop: '16px'
		},

		progressBarContainer: {
			width: '100%',
			background: '#ddd',
			borderRadius: 4,
			marginTop: 4
		},

		progressBar: {
			background: '#28a745',
			color: 'white',
			textAlign: 'center',
			padding: 4,
			borderRadius: 4,
			transition: 'width 0.3s'
		},

		uploadedFiles: {
			marginTop: 16,
			fontSize: 10
		},

		errorContainer: {
			marginTop: 16,
			color: 'red'
		},

		loader: {
			border: `4px solid ${Colors.lightGrey}`,
			borderTop: `4px solid ${Colors.blue}`,
			borderRadius: '50%',
			width: '20px',
			height: '20px',

			animation: 'spin 2s linear infinite'
		},
		justifyContent: {
			justifyContent: 'space-between'
		},
		fileItemStatus: {
			fontSize: '12px',
			color: Colors.darkGrey,
			display: 'flex',
			textAlign: 'right',
			width: '27%',
			justifyContent: 'center',
			'& span': {
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				textAlign: 'left',
				'& div': {
					marginRight: '5px'
				}
			}
		},
		fileItemName: {
			display: 'flex',
			width: '70%',
			fontSize: '15px',
			alignItems: 'end'
		},
		fileItemDetailsSummary: {
			'&:hover': {
				cursor: 'default '
			}
		}
	})

	const { pageData, authUser } = useContext<AppContextProps>(AppStateContext)
	const [addTransitionClassContainer, setAddTransitionClassContainer] =
		useState<boolean>(false)

	// blob
	// const [files, setFiles] = useState<File[]>([])
	const prevFilesRef = useRef<Set<File>>()
	const [uploading, setUploading] = useState<boolean>(false)
	const [progress, setProgress] = useState<Record<string, number>>({})
	const [errors, setErrors] = useState<string[]>([])
	const [uploadedFilesMaplocal, setUploadedFilesMapLocal] =
		useState<Map<string, string>>(uploadedFilesMap)

	// relative to maxFiles by input
	const [localFilesSizeWarning, setLocalFilesSizeWarning] =
		useState<boolean>(false)
	// relative to maxFiles by request / formulaire
	const [globalFilesSizeWarning, setGlobalFilesSizeWarning] =
		useState<boolean>(false)

	const fetchNewSasToken = async (): Promise<
		| {
				sasToken: string
				expiresOn: string
				blobUri: string
				containerName: string
		  }
		| undefined
	> => {
		if (!authUser) return undefined
		try {
			// const requestsRepository = new RequestsRepository()
			const repereIntegrationRepository = new RepereIntegrationRepository()

			const sasTokenData = await repereIntegrationRepository.getSASBlobStorage(
				authUser.profile?.id
			)

			localStorage.setItem(
				'sasTokenData',
				JSON.stringify({
					sasToken: sasTokenData.sasToken,
					expiresOn: sasTokenData.expiresOn,
					blobUri: sasTokenData.blobUri,
					containerName: sasTokenData.containerName
				})
			)

			return sasTokenData
		} catch (error) {
			console.error('Erreur lors de la récupération du SAS Token', error)
			setErrors((prev) => [
				...prev,
				`Erreur lors de la récupération du SAS Token`
			])
		}
	}

	// Récupère le SAS si encore valide
	const getCachedSasToken = (): {
		sasToken: string
		expiresOn: string
		blobUri: string
		containerName: string
	} | null => {
		const raw = localStorage.getItem('sasTokenData')

		if (!raw) return null

		try {
			const { sasToken, expiresOn, blobUri, containerName } = JSON.parse(raw)
			if (Date.now() < new Date(expiresOn).getTime()) {
				return { sasToken, expiresOn, blobUri, containerName }
			} else {
				localStorage.removeItem('sasTokenData')
				return null
			}
		} catch (err) {
			// En cas de données corrompues ou mal formées
			localStorage.removeItem('sasTokenData')
			return null
		}
	}

	const addTimestampToFilename = (filename: string) => {
		const dotIndex = filename.lastIndexOf('.')
		if (dotIndex === -1) return `${filename}_${Date.now()}`
		const namePart = filename.substring(0, dotIndex)
		const extension = filename.substring(dotIndex)
		return `${namePart}_${Date.now()}${extension}`
	}

	const sanitizeFileName = (filename: string): string => {
		// Décoder d'abord si le nom est URL-encoded
		let decodedFilename: string
		try {
			decodedFilename = decodeURIComponent(filename)
		} catch (error) {
			// Si le décodage échoue, utiliser le nom original
			decodedFilename = filename
		}

		// Séparer le nom et l'extension
		const dotIndex = decodedFilename.lastIndexOf('.')
		let name = decodedFilename
		let extension = ''

		if (dotIndex > 0) {
			name = decodedFilename.substring(0, dotIndex)
			extension = decodedFilename.substring(dotIndex)
		}

		// Caractères interdits à remplacer par « _ »
		const forbiddenChars = /[:;,'"\\/|?*<>()#%{}\[\]~&]/g
		name = name.replace(forbiddenChars, '_')

		// Remplacer les espaces multiples par un seul « _ »
		name = name.replace(/\s+/g, '_')

		// Retirer les « _ » en début et fin
		name = name.replace(/^_+|_+$/g, '')

		return name + extension
	}

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			const newErrors: string[] = []
			let localFilesSizeWarning = false
			let globalFilesSizeWarning = false

			// sanitize file names to prevent issues with Azure Blob Storage
			acceptedFiles = acceptedFiles.map((file) => {
				const sanitizedFileName = sanitizeFileName(file.name)
				return new File([file], sanitizedFileName, { type: file.type })
			})

			// Éliminer les doublons (déjà présents ou déjà uploadés)
			const filteredFiles = acceptedFiles.filter(
				(file) => !files.has(file) && !uploadedFilesMap.has(file.name)
			)

			// Vérifier taille, type
			const validatedFiles = filteredFiles.filter((file) => {
				if (file.size > config.request.blobFile.maxSize) {
					newErrors.push(`"${file.name}" dépasse la taille maximale autorisée.`)
					return false
				}

				if (
					!file.name.toLowerCase().match(config.request.blobFile.acceptRegex)
				) {
					newErrors.push(`"${file.name}" est d'un type non autorisé.`)
					return false
				}

				return true
			})

			// Étape 2 : calculs de limites
			const alreadyUploadedCount = totalFilesCount ?? 0 // <- passé en prop
			const localRemaining = maxFiles
				? maxFiles - files.size
				: validatedFiles.length
			const globalRemaining =
				config.request.blobFile.maxFilesCount - alreadyUploadedCount

			const effectiveLimit = Math.min(localRemaining, globalRemaining)

			const acceptedLimitedFiles = validatedFiles.slice(0, effectiveLimit)
			const ignoredFiles = validatedFiles.slice(effectiveLimit)

			if (ignoredFiles.length > 0) {
				if (localRemaining < globalRemaining) {
					localFilesSizeWarning = true
					// newErrors.push(
					// 	`Vous avez atteint la limite de fichiers pour ce champ (${maxFiles}). Supprimez un fichier si vous souhaitez en ajouter un nouveau.`
					// )
				} else {
					globalFilesSizeWarning = true
					// newErrors.push(
					// 	`Vous avez atteint la limite globale de fichiers pour cette demande (${config.request.blobFile.maxFilesCount}). Supprimez un fichier ailleurs pour en ajouter.`
					// )
				}
			}

			if (globalRemaining == 0) {
				globalFilesSizeWarning = true
			}

			if (newErrors.length > 0) {
				setErrors((prev) => {
					const filteredNewErrors = newErrors.filter(
						(err) => !prev.includes(err)
					)
					return [...prev, ...filteredNewErrors]
				})
				onSetHasError(true, name)
				return // stop ici
			} else {
				onSetHasError(false, name)
			}

			// Ajouter seulement les fichiers valides et autorisés
			const newSet = new Set([...Array.from(files), ...acceptedLimitedFiles])
			onSetFiles(newSet)
			setLocalFilesSizeWarning(localFilesSizeWarning)
			setGlobalFilesSizeWarning(globalFilesSizeWarning)
		},
		[files, uploadedFilesMap, name, totalFilesCount, onSetFiles, onSetHasError]
	)

	const { getRootProps, getInputProps } = useDropzone({
		onDrop,
		multiple: true,
		// maxFiles,
		disabled: disabled || files.size >= maxFiles!
	})

	useEffect(() => {
		if (
			prevFilesRef.current !== files &&
			prevFilesRef.current?.size !== files.size
		) {
			uploadNewFiles()
		}
		prevFilesRef.current = files
	}, [files])

	const uploadNewFiles = useCallback(async () => {
		if (files.size === 0 || uploading) return

		setUploading(true)
		setErrors([])
		setProgress({})

		// const sasStoragePath = await fetchNewSasToken()
		const sasTokenData = getCachedSasToken() ?? (await fetchNewSasToken())

		if (!sasTokenData) {
			setUploading(false)
			// !error
			onSetHasError(true, name)
			return
		}
		//https://vdlresodataqc01sa.blob.core.windows.net/?
		const blobServiceClient = new BlobServiceClient(
			`${sasTokenData?.blobUri + '?' + sasTokenData.sasToken}`
		)

		//mondossier-contacts-files
		const containerClient: ContainerClient =
			blobServiceClient.getContainerClient(`${sasTokenData?.containerName}`)

		let uploadProgress: Map<string, string> = new Map(uploadedFilesMap)

		for (const file of files) {
			if (uploadedFilesMaplocal.has(file.name)) {
				continue
			}

			// Ajoute une entrée vide pour indiquer que l'upload a commencé
			// uploadProgress.set(file.name, '')

			const blobClient = containerClient.getBlockBlobClient(
				`${authUser!.profile?.id}/${addTimestampToFilename(file.name)}`
			)
			try {
				await blobClient.uploadData(file, {
					blobHTTPHeaders: { blobContentType: file.type },
					onProgress: (progressEvent) => {
						const percentCompleted = Math.round(
							(progressEvent.loadedBytes / file.size) * 100
						)
						setProgress((prevProgress) => ({
							...prevProgress,
							[file.name]: percentCompleted
						}))
					}
				})

				// Url sans params depuis blobClient.url
				const fileUrl = blobClient.url?.split('?')[0]

				// Met à jour la map avec l'URL une fois l'upload terminé
				uploadProgress.set(file.name, fileUrl)

				setUploadedFilesMapLocal((prevMap) => {
					const newMap = new Map(prevMap)
					newMap.set(file.name, fileUrl)
					return newMap
				})
			} catch (error) {
				console.error(`Erreur lors de l'upload de `, file.name, error)
				setErrors((prev) => [
					...prev,
					`Erreur lors de l'upload de ${file.name}`
				])
				// !error
				onSetHasError(true, name)
			}
		}

		setUploadedFilesMap(new Map(uploadProgress))

		// Vérifie si tous les fichiers sont uploadés
		const allUploadsFinished = Array.from(uploadProgress.values()).every(
			(url) => !!url
		)
		if (allUploadsFinished) {
			onSetHasError(false, name)
		}

		setUploading(false)
	}, [files, uploadedFilesMap, uploading])

	useEffect(() => {
		// keep transition style if there is a file
		if (files.size > 0) {
			setAddTransitionClassContainer(true)
		}
	}, [])

	const displayThumbnail = (requestImg: RequestImgUrl | undefined) => {
		if (!requestImg) {
			return <></>
		}
		if (requestImg.isPDF) {
			return (
				<img
					id={requestImg.id}
					src={pdfIcon}
					alt={requestImg.name}
					className={pageUtils.classes.img}
				/>
			)
		} else if (requestImg.isWord) {
			return (
				<img
					id={requestImg.id}
					src={wordIcon}
					alt={requestImg.name}
					className={pageUtils.classes.img}
				/>
			)
		} else if (requestImg.isExcel) {
			return (
				<img
					id={requestImg.id}
					src={excelIcon}
					alt={requestImg.name}
					className={pageUtils.classes.img}
				/>
			)
		}
		return (
			<img
				id={requestImg.id}
				src={requestImg.url}
				alt={requestImg.name}
				className={pageUtils.classes.img}
			/>
		)
	}

	const getRequestImg = (file: File): RequestImgUrl => {
		let url: string = URL.createObjectURL(file)
		return {
			url,
			id: `${file.size}-${file.name}`,
			name: file.name,
			isPDF: file.type.includes('pdf'),
			isWord: !!file?.name.toLowerCase().match(/\.(doc|docx)$/),
			isExcel: !!file?.name.toLowerCase().match(/\.(xls|xlsx)$/)
		}
	}

	const handleRemoveFile = (file: File) => (e) => {
		e.preventDefault()

		let NewUploadedFilesMaplocal = new Map(uploadedFilesMaplocal)
		NewUploadedFilesMaplocal.delete(file.name)
		setUploadedFilesMapLocal(new Map(NewUploadedFilesMaplocal))

		// delete file from files
		const newFiles = new Set(files)
		newFiles.delete(file)
		onSetFiles(newFiles)

		// delete file from uploadedFilesMap
		const newMap = new Map(uploadedFilesMap)
		newMap.delete(file.name)
		setUploadedFilesMap(newMap)

		setGlobalFilesSizeWarning(false)
	}

	return (
		<div className={joinClasses([classes.container, 'fileContainer'])}>
			{title && (
				<h3 className={pageUtils.classes.h3}>{formatStrapiText(title)}</h3>
			)}
			{((isSummary && files.size > 0) || (!isSummary && subtitle)) && (
				<h4
					className={joinClasses([
						pageUtils.classes.h4,
						!title ? pageUtils.classes.marginTop0 : '',
						hasError && required ? pageUtils.classes.error : '',
						'fileSubtitle'
					])}
					style={{ fontSize: isSummary ? '14px' : '16px' }}
				>
					{formatStrapiText(subtitle)}{' '}
					{required && !isSummary && <RequiredIcon />}
				</h4>
			)}

			{!isSummary && description && (
				<span
					className={joinClasses([
						pageUtils.classes.description,
						hasError ? pageUtils.classes.error : ''
					])}
				>
					<Text content={description} />
				</span>
			)}

			{hasError && uploading && (
				<Error
					hasError
					errorText="Veuillez patienter, vos fichiers sont en cours de téléversement..."
				/>
			)}

			{hasError && required && !uploading && (
				<Error hasError errorText={pageData?.assets?.required_file} />
			)}

			{/* blob */}
			{!isSummary && (
				<div
					{...getRootProps()}
					className={joinClasses([
						hasError ? pageUtils.classes.errorBorder : '',
						pageUtils.classes.dragAndDropArea,
						// isDragOver ? pageUtils.classes.dragOver : '',
						disabled || (maxFiles && files.size >= maxFiles)
							? pageUtils.classes.disabled
							: ''
					])}
				>
					<input
						disabled={disabled || files.size >= maxFiles!}
						{...getInputProps()}
						id={name}
					/>
					<p style={{ marginBottom: '0.25rem' }}>
						{pageData?.assets?.page_requestInfo_drop_file}
					</p>
					<Link
						id={name}
						className={joinClasses([
							pageUtils.classes.btn,
							pageUtils.classes.inputFileBtn,
							(maxFiles && files.size >= maxFiles) || disabled
								? pageUtils.classes.btnDisabled
								: ''
						])}
						to="#"
					>
						{formatStrapiText(pageData?.assets?.page_requestInfo_inputFile)}
					</Link>
				</div>
			)}

			<TransitionGroup
				className={joinClasses([
					addTransitionClassContainer ? pageUtils.classes.fileList : '',
					'fileList'
				])}
				translate="no"
			>
				{files.size > 0 &&
					[...files].map((file: File, key: number) => (
						<CSSTransition
							timeout={500}
							key={key}
							classNames={{
								enter: pageUtils.classes.enter,
								enterActive: pageUtils.classes.enterActive,
								exit: pageUtils.classes.exit,
								exitActive: pageUtils.classes.exitActive
							}}
							onEnter={() => setAddTransitionClassContainer(true)}
							onExited={() => {
								setAddTransitionClassContainer((prev) => {
									// Vérifier après un délai pour éviter les race conditions
									setTimeout(() => {
										if (files.size === 0) {
											setAddTransitionClassContainer(false)
										}
									}, 0)
									return prev
								})
							}}
						>
							<div
								className={joinClasses([
									pageUtils.classes.fileItem,
									'fileItem'
								])}
								key={key}
								translate="no"
							>
								<div
									className={joinClasses([
										pageUtils.classes.fileItemDetails,
										classes.justifyContent,
										isSummary ? classes.fileItemDetailsSummary : '',
										'fileItemDetails'
									])}
									translate="no"
									// onClick={onOpenFileOnBrowser(file)}
								>
									<div
										className={joinClasses([
											classes.fileItemName,
											'fileItemName',
											'no-translate'
										])}
										style={{
											display: !toPrint ? 'flex' : 'block'
										}}
										translate="no"
										lang="fr"
									>
										{!toPrint && displayThumbnail(getRequestImg(file))}
										{file.name} {!toPrint && `(${formatFileSize(file.size)})`}{' '}
									</div>
									{!toPrint && (
										<div className={classes.fileItemStatus} translate="no">
											{errors.includes(
												`Erreur lors de l'upload de ${file.name}`
											) ||
											errors.includes(
												'Erreur lors de la récupération du SAS Token'
											) ? (
												<span>
													{' '}
													<div>❌</div> Échec du téléversement
												</span>
											) : uploadedFilesMaplocal.has(file.name) ? (
												<>
													<span>
														{' '}
														<div>✅</div> Téléversé avec succès !
													</span>
												</>
											) : (
												<span>
													<div className={classes.loader} /> Téléversement en
													cours...
												</span>
											)}
										</div>
									)}
								</div>
								{!isSummary && (
									<div
										className={pageUtils.classes.fileItemCloseIcon}
										onClick={handleRemoveFile(file)}
									>
										<Icon
											src={closeIcon}
											className={pageUtils.classes.imgCloseIcon}
										/>
									</div>
								)}
							</div>
						</CSSTransition>
					))}
			</TransitionGroup>
			{errors.length > 0 && (
				<div style={{ marginTop: '16px' }}>
					<Notification
						type="error"
						hasHtml
						text={`Un problème est survenu lors de l'envoi de votre document. Veuillez réessayer plus tard.`}
					/>
				</div>
			)}

			{!isSummary && localFilesSizeWarning && !globalFilesSizeWarning && (
				<>
					<br />
					<Notification
						text={`Seulement ${maxFiles} fichier(s) autorisé(s). Les fichiers supplémentaires ont été ignorés.`}
						type="warning"
						hasHtml
						showExitBtn
						onClickCancelBtn={() => setLocalFilesSizeWarning(false)}
					/>
				</>
			)}

			{!isSummary &&
			(fieldWarnings?.[name] == true ||
				(totalFilesCount &&
					totalFilesCount >= config.request.blobFile.maxFilesCount &&
					showFilesCountWarning)) ? (
				<>
					<br />
					<Notification
						text={
							pageData?.assets?.error_request_form_blob_uploader_max_documents
						}
						type="warning"
						hasHtml
					/>
				</>
			) : (
				''
			)}
		</div>
	)
}

export default BlobUploader
